import type { TSwitchAnimation } from "context/transitionAnimationContext";
import type { TCompanyRelation } from "types/authorization";
import type { FeatureFlags } from "types/featureFlags";

import { type CompanyModel } from "models/CompanyModel";
import { getPageLocation } from "routes/utils";
import { devLog } from "utils/devtools/devLogging";

import type { UserModel } from "../models/UserModel";

export type TPageId =
  | "AccessReport"
  | "AccessReview"
  | "AccessReviewReport"
  | "AccessReviewTemplatePage"
  | "AccessReviewTemplatesPage"
  | "AdminResourceAccessReview"
  | "AdminSubordinateAccessReview"
  | "AuditLogs"
  | "BulkActions"
  | "Bundles"
  | "CreateIntegration"
  | "CreateWorkflow"
  | "DirectoryConfigurationPage"
  | "EditWorkflow"
  | "Home"
  | "Integration"
  | "IntegrationResource"
  | "IntegrationResourceRole"
  | "Integrations"
  | "JitAnalysis"
  | "MyPermissions"
  | "MyRequests"
  | "NewRequest"
  | "NewTicket"
  | "PastRequests"
  | "PendingRequests"
  | "Permissions"
  | "Policies"
  | "Profile"
  | "Reporting"
  | "Requests"
  | "RequestsLog"
  | "ResourceAccessReport"
  | "Rules"
  | "RulesCreate"
  | "RulesEdit"
  | "Settings"
  | "SubordinateAccessReport"
  | "Tasks"
  | "User"
  | "Users"
  | "Workflows"
  | "WorkflowsCreate"
  | "WorkflowsEdit"
  | "WorkflowsWebhooks";

export type TShouldRenderFunction = (options: TShouldRenderOptions) => boolean;

interface IRoute {
  animationOrder?: number;
  hideSideNav?: boolean;
  inAnimation?: TAnimationObject;
  matches: string[];
  outAnimation?: TAnimationObject;
  Page: FC;
  pageId: TPageId;
  shouldRender?: (options: TShouldRenderOptions) => boolean;
  subRoutes?: Record<string, Route>;
}

type TAnimationObject = {
  name: TSwitchAnimation;
  reverse?: boolean;
  targets?: string[];
};

type TShouldRenderOptions = {
  company?: CompanyModel;
  flags?: FeatureFlags;
  hasPermission?: (relation: TCompanyRelation) => boolean;
  user: UserModel;
};

export class Route {
  public animationOrder?: number;

  public hideSideNav: boolean;
  public inAnimation?: TAnimationObject;
  public isRoleRestricted: boolean;
  public matches: string[];

  public outAnimation?: TAnimationObject;
  public Page: FC;
  public pageId: TPageId;

  public shouldRender: (options: TShouldRenderOptions) => boolean;

  public subRoutes?: Record<string, Route>;

  public get location() {
    const location = getPageLocation(this.pageId);
    if (!location) {
      devLog({ extra: { pageId: this.pageId }, message: "No location found for pageId" });
      return this.main;
    }

    return location;
  }

  public get main() {
    return this.matches[0];
  }

  public get matchesWithChildren() {
    const matches = [...this.matches];

    if (this.subRoutes) {
      Object.values(this.subRoutes).forEach((route) => {
        const subMatches = route.matchesWithChildren;

        this.matches.forEach((match) => {
          subMatches.forEach((subMatch) => {
            matches.push(match + subMatch);
          });
        });
      });
    }

    return matches;
  }

  constructor({
    animationOrder,
    hideSideNav = false,
    inAnimation,
    matches,
    outAnimation,
    Page,
    pageId,
    shouldRender,
    subRoutes,
  }: IRoute) {
    this.matches = matches;
    this.subRoutes = subRoutes;
    this.inAnimation = inAnimation;
    this.outAnimation = outAnimation;
    this.Page = Page;
    this.pageId = pageId;
    this.animationOrder = animationOrder;
    this.isRoleRestricted = shouldRender !== undefined;
    this.shouldRender = shouldRender ?? (() => true);
    this.hideSideNav = hideSideNav;
  }
}
